import React from 'react';
import type { FormikProps } from 'formik';
import Grid2 from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useApolloClient } from '@apollo/client/react';
import { ROUTES } from 'config/Nav';
import { encodeFromURL } from 'lib/redirects';
import { CAMPAIGN_TYPES } from 'lib/graphql/queries/campaign';
import type { CampaignType } from 'types/campaign';
import type { CampaignCheckoutValues } from 'types/campaignCheckout';
import { CheckoutForm, OrderSummary } from 'molecules';
import type { OrderSummaryItem } from 'molecules/OrderSummary/OrderSummary';
import useStyles from './styles';
import { Button, PaymentType, User } from '@elevator/ui';
import clsx from 'clsx';

export type CampaignCheckoutFieldsProps = FormikProps<CampaignCheckoutValues> & {
  paymentType: PaymentType;
  setPaymentType: (value: PaymentType) => void;
  totalCost: number;
  typeId: string;
  currentUser: User | null;
  onBeforeAuthRedirect?: () => void | Promise<void>;
};

/** Guest checkout gate: log in or create account before payment. */
function GuestAuthPrompt({
  onBeforeRedirect,
}: {
  onBeforeRedirect?: () => void | Promise<void>;
}) {
  const { classes } = useStyles();

  const handleAuthRouting = async (route: string) => {
    await onBeforeRedirect?.();
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set('resume', '1');
    const b64EncodedFromURL = encodeFromURL(returnUrl.toString());
    window.location.replace(
      `${route}${b64EncodedFromURL ? `?from=${b64EncodedFromURL}` : ''}`,
    );
  };

  return (
    <Stack className={classes.authPrompt} spacing={2} data-component="CampaignAuthPrompt">
      <Typography variant="body1" align="center">
        Please create an account or login to submit Ad Campaign
      </Typography>
      <Stack direction="row" spacing={2} className={classes.authPromptButtons}>
        <Button
          type="button"
          size="large"
          className={classes.authPromptButton}
          variant="outlined"
          onClick={() => void handleAuthRouting(ROUTES.AUTH.CREATE_ACCOUNT.to)}
        >
          Create Account
        </Button>
        <Button
          type="button"
          size="large"
          className={classes.authPromptButton}
          variant="contained"
          onClick={() => void handleAuthRouting(ROUTES.AUTH.LOG_IN.to)}
        >
          Log In
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * Campaign checkout form body: auth gate for guests, shared CheckoutForm molecule, order summary.
 * reCAPTCHA and action buttons render below {@link stepMain} in {@link CheckoutStep}.
 */
const CampaignCheckoutFields = (props: CampaignCheckoutFieldsProps) => {
  const { classes } = useStyles();
  const {
    currentUser,
    paymentType,
    setPaymentType,
    totalCost,
    typeId,
    onBeforeAuthRedirect,
  } = props;

  const isGuest = !currentUser;
  const apolloClient = useApolloClient();

  const orderSummaryItems: OrderSummaryItem[] = React.useMemo(() => {
    const data = apolloClient.readQuery<{ campaignTypes: CampaignType[] }>({
      query: CAMPAIGN_TYPES,
    });
    const selectedType = data?.campaignTypes?.find((type) => type.typeId === typeId);
    const name = selectedType?.name ?? 'Campaign';
    return [{ id: typeId || 'campaign', name, cost: totalCost }];
  }, [apolloClient, typeId, totalCost]);

  return (
    <Grid2
      container
      className={clsx(classes.formGrid, isGuest && classes.formGridGuest)}
      data-component="CampaignCheckoutFields"
    >
      <Grid2 size={{ xs: 12 }}>
        <OrderSummary
          title="Order Summary"
          items={orderSummaryItems}
          total={totalCost}
          paymentType={paymentType}
          emptyMessage="No campaign selected."
        />
      </Grid2>
      {isGuest && (
        <Grid2 size={{ xs: 12 }} className={classes.authPromptRegion}>
          <GuestAuthPrompt onBeforeRedirect={onBeforeAuthRedirect} />
        </Grid2>
      )}
      {!isGuest && (
        <Grid2 size={{ xs: 12 }}>
          <CheckoutForm paymentType={paymentType} setPaymentType={setPaymentType} />
        </Grid2>
      )}
    </Grid2>
  );
};

export default CampaignCheckoutFields;
